import React from 'react';
import { Box, Grid, Card, CardContent, Typography, CardMedia, IconButton, Chip, Divider } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import BlockIcon from '@mui/icons-material/Block';
import { useLanguage } from '../contexts/LanguageContext';

const BannedHeroes = ({ heroes, bannedHeroes, onBannedHeroesChange }) => {
  const { t } = useLanguage();
  const handleHeroClick = (hero) => {
    if (!bannedHeroes.some(h => h.id === hero.id)) {
      onBannedHeroesChange([...bannedHeroes, hero]);
    }
  };

  const handleUnbanHero = (heroId) => {
    onBannedHeroesChange(bannedHeroes.filter(h => h.id !== heroId));
  };

  const availableHeroes = heroes.filter(
    hero => !bannedHeroes.some(banned => banned.id === hero.id)
  );

  // Group heroes by role
  const heroGroups = {
    VANGUARD: availableHeroes.filter(hero => hero.role === 'VANGUARD'),
    DUELIST: availableHeroes.filter(hero => hero.role === 'DUELIST'),
    STRATEGIST: availableHeroes.filter(hero => hero.role === 'STRATEGIST')
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'VANGUARD': return '#3498db';
      case 'DUELIST': return '#e74c3c';
      case 'STRATEGIST': return '#2ecc71';
      default: return '#fbdb2b';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: 'white',
          fontWeight: 'bold',
          position: 'relative',
          paddingBottom: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '40px',
            height: '3px',
            backgroundColor: '#fbdb2b',
            borderRadius: '2px'
          }
        }}
      >
        {t('bannedHeroes')} ({bannedHeroes.length})
      </Typography>
      
      {bannedHeroes.length > 0 ? (
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {bannedHeroes.map((hero) => (
            <Grid item xs={4} sm={3} md={2} lg={1.5} key={hero.id}>
              <Card sx={{
                backgroundColor: 'rgba(0,0,0,0.4)',
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                }
              }}>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: 4,
                      top: 4,
                      backgroundColor: 'rgba(211, 47, 47, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 1)',
                      },
                      zIndex: 1
                    }}
                    onClick={() => handleUnbanHero(hero.id)}
                  >
                    <ClearIcon sx={{ color: 'white', fontSize: '0.9rem' }} />
                  </IconButton>
                  <CardMedia
                    component="img"
                    height="80"
                    image={hero.image}
                    alt={hero.name}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'top',
                      backgroundSize: 'cover',
                      backgroundPosition: 'top',
                      p: 0.25,
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      filter: 'grayscale(80%)'
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(211, 47, 47, 0.7)',
                      borderRadius: '50%',
                      width: 30,
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <BlockIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                  </Box>
                  <CardContent sx={{ py: 0.5, px: 1, '&.MuiCardContent-root': { padding: '4px 8px' } }}>
                    <Typography 
                      variant="caption" 
                      align="center" 
                      display="block"
                      sx={{ color: 'white' }}
                    >
                      {hero.name}
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box 
          sx={{ 
            p: 2, 
            textAlign: 'center', 
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
            border: '1px dashed rgba(255,255,255,0.2)',
            mb: 3
          }}
        >
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            {t('noBannedHeroesYet')}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.1)' }} />

      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          mt: 2, mb: 2,
          color: 'white',
          fontWeight: 'bold',
          position: 'relative',
          paddingBottom: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '40px',
            height: '3px',
            backgroundColor: '#fbdb2b',
            borderRadius: '2px'
          }
        }}
      >
        {t('availableHeroes')}
      </Typography>

      {Object.entries(heroGroups).map(([role, roleHeroes]) => (
        <Box key={role} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Chip 
              label={t(role.toLowerCase())}
              sx={{ 
                backgroundColor: getRoleColor(role),
                color: 'white',
                fontWeight: 'bold',
                mr: 1
              }} 
            />
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              {role === 'VANGUARD' ? t('tank') : role === 'DUELIST' ? t('damage') : t('support')}
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {roleHeroes.map((hero) => (
              <Grid item xs={4} sm={3} md={2} lg={1.5} key={hero.id}>
                <Card
                  onClick={() => handleHeroClick(hero)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 5px 15px rgba(${getRoleColor(hero.role).replace('#', '')}, 0.3)`,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="80"
                    image={hero.image}
                    alt={hero.name}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'top',
                      backgroundSize: 'cover',
                      backgroundPosition: 'top',
                      p: 0.25,
                      backgroundColor: 'rgba(0,0,0,0.2)'
                    }}
                  />
                  <CardContent sx={{ py: 0.5, px: 1, '&.MuiCardContent-root': { padding: '4px 8px' } }}>
                    <Typography 
                      variant="caption" 
                      align="center" 
                      display="block"
                      sx={{ 
                        color: 'white',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {hero.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default BannedHeroes;